/**
 * viewRuntime — the ONE runtime a list surface uses to turn
 * (rows + saved-view config) into (rendered rows + a render status).
 *
 * WHY this exists: the auditor flagged that filter/view logic had
 * fragmented into overlapping modules — filterEngine.ts (compound
 * AND/OR operators), viewFilter.ts (flat filter + sort), viewConfig.ts
 * (rich SavedView + its OWN operator table) and savedViewModel.ts —
 * and that AccountsCrmView evaluated filters through a parallel path
 * with no empty / no-match / error handling. This module DEDUPES
 * rather than adds:
 *
 *   - Operator evaluation delegates to filterEngine.ts. viewConfig
 *     filter trees are TRANSLATED into filterEngine trees (cmp → op),
 *     never re-evaluated by a second switch statement.
 *   - Sorting delegates to viewFilter.applySort.
 *   - Malformed nodes (unknown operator, garbage children) are skipped
 *     with a console warning instead of crashing the list.
 *   - `deriveViewStatus` disambiguates "collection is empty" from
 *     "filters matched nothing" from "the filter itself failed".
 *   - `debounceFilter` keeps keystroke-driven live filtering cheap.
 *   - `resolveActiveView` guards against a persisted active-view id
 *     pointing at a deleted view: it always resolves to SOMETHING.
 *
 * PURE except for console.warn on malformed input: no store access,
 * no globals, inputs never mutated.
 */

import {
  applyFilter,
  type FilterGroup as EngineGroup,
  type FilterLeaf as EngineLeaf,
  type FilterOp as EngineOp,
} from './filterEngine';
import { applySort, type ViewSort } from './viewFilter';
import {
  defaultView,
  isFilterNode,
  type FilterLeaf as ConfigLeaf,
  type FilterNode,
  type SavedView,
} from './viewConfig';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A list row: any plain record. Fields are looked up by name. */
export type Row = Record<string, unknown>;

/**
 * The slice of a rich SavedView the runtime interprets. Structural, so
 * a full `viewConfig.SavedView` passes directly — no adapter needed.
 */
export interface ViewConfig {
  filter: FilterNode;
  sort?: ViewSort;
}

/** What {@link applyView} returns: kept rows plus honest counts. */
export interface ViewResult<T extends Row = Row> {
  rows: T[];
  /** How many rows passed the filter. */
  matched: number;
  /** How many rows were considered (the whole collection). */
  total: number;
}

/** The full render state a list surface derives per frame. */
export type ViewState = {
  config: ViewConfig;
  rows: Row[];
  status: 'idle' | 'loading' | 'ready' | 'empty' | 'error';
  error?: string;
};

// ---------------------------------------------------------------------------
// Config → engine translation (guarded — never throws)
// ---------------------------------------------------------------------------

function warn(msg: string): void {
  // eslint-disable-next-line no-console
  if (typeof console !== 'undefined') console.warn(`[viewRuntime] ${msg}`);
}

/** cmp names shared 1:1 between viewConfig leaves and filterEngine ops. */
const OP_MAP: Partial<Record<string, EngineOp>> = {
  eq: 'eq',
  neq: 'neq',
  contains: 'contains',
  gt: 'gt',
  lt: 'lt',
  in: 'in',
  isEmpty: 'isEmpty',
  notEmpty: 'notEmpty',
};

/**
 * Translate one viewConfig leaf into a filterEngine leaf. Unknown
 * operators and malformed relDate specs return null (skip + warn) so a
 * corrupt persisted view degrades gracefully instead of crashing.
 */
function toEngineLeaf(leaf: ConfigLeaf): EngineLeaf | null {
  if (typeof leaf.field !== 'string' || leaf.field.trim() === '') {
    warn('filter clause without a field name — skipped');
    return null;
  }

  if (leaf.cmp === 'relDate') {
    const spec =
      typeof leaf.value === 'number'
        ? { days: leaf.value, dir: 'past' as const }
        : (leaf.value as { days?: number; dir?: 'past' | 'future' } | null);
    const days = spec?.days;
    if (typeof days !== 'number' || !Number.isFinite(days)) {
      warn(`relDate on "${leaf.field}" has no valid day count — skipped`);
      return null;
    }
    const op: EngineOp =
      (spec?.dir ?? 'past') === 'future' ? 'relativeDateNext' : 'relativeDateWithin';
    return { attr: leaf.field, op, value: days };
  }

  const op = OP_MAP[leaf.cmp as string];
  if (!op) {
    warn(`unknown operator "${String(leaf.cmp)}" on "${leaf.field}" — clause skipped`);
    return null;
  }
  return { attr: leaf.field, op, value: leaf.value };
}

/**
 * Translate a viewConfig tree into a filterEngine tree, dropping
 * malformed nodes with a warning. An unrecognizable ROOT becomes the
 * identity group (matches everything) — an honest "no filter".
 */
export function toEngineGroup(node: FilterNode | null | undefined): EngineGroup {
  if (!node || typeof node !== 'object' || !Array.isArray(node.children)) {
    if (node !== null && node !== undefined) warn('malformed filter root — treated as no filter');
    return { combinator: 'AND', children: [] };
  }
  const combinator = node.op === 'or' ? 'OR' : 'AND';
  const children: (EngineLeaf | EngineGroup)[] = [];
  for (const child of node.children) {
    if (child === null || typeof child !== 'object') {
      warn('malformed filter node — skipped');
      continue;
    }
    if (isFilterNode(child)) {
      children.push(toEngineGroup(child));
    } else if ('field' in child) {
      const leaf = toEngineLeaf(child);
      if (leaf) children.push(leaf);
    } else {
      warn('unrecognized filter node shape — skipped');
    }
  }
  return { combinator, children };
}

// ---------------------------------------------------------------------------
// applyView — the single canonical filter + sort path
// ---------------------------------------------------------------------------

/**
 * Run a view config over rows: translate the filter tree, delegate row
 * filtering to filterEngine.applyFilter and ordering to
 * viewFilter.applySort, and report honest counts so the caller can
 * tell "empty collection" (total === 0) from "filters matched nothing"
 * (total > 0, matched === 0). Never throws on malformed configs. Pure.
 */
export function applyView<T extends Row>(rows: T[], config: ViewConfig): ViewResult<T> {
  const total = rows.length;
  const group = toEngineGroup(config?.filter);
  const kept = applyFilter(rows, group);
  const ordered = applySort(kept, config?.sort);
  return { rows: ordered, matched: kept.length, total };
}

// ---------------------------------------------------------------------------
// deriveViewStatus — empty vs no-match vs error disambiguation
// ---------------------------------------------------------------------------

/** The counts deriveViewStatus needs (a ViewResult qualifies). */
export interface ViewResultCounts {
  matched: number;
  total: number;
}

/**
 * Collapse (result, loading, error) into one render status. Precedence:
 * error beats everything (a broken filter must be SEEN), then loading,
 * then idle (no result yet), then empty (the COLLECTION has zero rows),
 * then ready. "Filters matched nothing" is a flavor of ready — the
 * collection exists, the lens hid it — detected via {@link isNoMatch}
 * so the UI can offer "clear filters" instead of "add your first row".
 */
export function deriveViewStatus(
  result: ViewResultCounts | null | undefined,
  isLoading: boolean,
  err?: string | null,
): ViewState['status'] {
  if (err) return 'error';
  if (isLoading) return 'loading';
  if (!result) return 'idle';
  if (result.total === 0) return 'empty';
  return 'ready';
}

/** True when rows exist but the active filters hid every one of them. */
export function isNoMatch(result: ViewResultCounts | null | undefined): boolean {
  return !!result && result.total > 0 && result.matched === 0;
}

// ---------------------------------------------------------------------------
// debounceFilter — keystroke-safe live filtering
// ---------------------------------------------------------------------------

/**
 * Debounce a filter callback: a burst of calls fires `fn` ONCE with the
 * LAST input after `ms` of quiet. `.cancel()` drops any pending call
 * (unmount cleanup). Timer-based, no globals beyond setTimeout.
 */
export function debounceFilter<I>(
  fn: (input: I) => void,
  ms: number,
): ((input: I) => void) & { cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const debounced = (input: I): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = undefined;
      fn(input);
    }, ms);
  };
  const out = debounced as ((input: I) => void) & { cancel: () => void };
  out.cancel = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  };
  return out;
}

// ---------------------------------------------------------------------------
// resolveActiveView — default view + deleted-id fallback guard
// ---------------------------------------------------------------------------

/**
 * Resolve which view a list should render when the remembered active
 * id is missing (deleted view, first run, cleared storage). Order:
 *
 *   1. the user's own default view (id ends with ":default"),
 *   2. the first team-shared view,
 *   3. the first view the user owns,
 *   4. the supplied fallback, else a synthesized identity view.
 *
 * NEVER returns undefined — the list always has a lens. Pure.
 */
export function resolveActiveView(
  savedViews: SavedView[],
  userId: string,
  fallback?: SavedView,
): SavedView {
  const views = Array.isArray(savedViews) ? savedViews : [];
  const mine = views.filter((v) => v.ownerId === userId);
  const myDefault = mine.find((v) => v.id.endsWith(':default'));
  if (myDefault) return myDefault;
  const shared = views.find((v) => v.visibility === 'team');
  if (shared) return shared;
  if (mine.length > 0) return mine[0];
  return fallback ?? defaultView('records', userId, []);
}
